import React, { useEffect, useRef, useState } from "react";
import {
    Pencil,
    Highlighter,
    Type,
    ArrowRight,
    RotateCcw,
    RotateCw,
    Save,
    FilePlus2,
    X,
} from "lucide-react";

/**
 * Lightweight image annotation modal.
 * Supports: freehand pen, highlight rectangles, arrows, and text.
 * Produces a flattened PNG blob on save.
 */
const ImageAnnotatorModal = ({ imageFile, onClose, onSave }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const baseCanvasRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const containerRef = useRef(null);
    const textInputRef = useRef(null);

    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [tool, setTool] = useState("pen"); // pen | highlight | arrow | text
    const [strokeColor, setStrokeColor] = useState("#ff3b30");
    const [strokeWidth, setStrokeWidth] = useState(4);
    const [highlightColor, setHighlightColor] = useState("#fff59d");

    const [actions, setActions] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const [isPointerDown, setIsPointerDown] = useState(false);
    const [activeDraft, setActiveDraft] = useState(null);
    const [pendingText, setPendingText] = useState({ visible: false, x: 0, y: 0, value: "" });

    useEffect(() => {
        // Create an object URL and draw the image; be careful with Strict Mode double-effects.
        const objectUrl = URL.createObjectURL(imageFile);
        setImageUrl(objectUrl);
        const img = new Image();
        img.onload = () => {
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;
            setImageSize({ width, height });
            const baseCanvas = baseCanvasRef.current;
            const overlayCanvas = overlayCanvasRef.current;
            [baseCanvas, overlayCanvas].forEach((c) => {
                if (!c) return;
                c.width = width;
                c.height = height;
                const ctx = c.getContext("2d");
                if (ctx) ctx.imageSmoothingEnabled = true;
            });
            const ctx = baseCanvas.getContext("2d");
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
        };
        img.onerror = () => {
            // Fallback for rare cases where object URL fails due to early revocation
            const reader = new FileReader();
            reader.onload = () => {
                setImageUrl(reader.result);
                const fimg = new Image();
                fimg.onload = () => {
                    const width = fimg.naturalWidth || fimg.width;
                    const height = fimg.naturalHeight || fimg.height;
                    setImageSize({ width, height });
                    const baseCanvas = baseCanvasRef.current;
                    const overlayCanvas = overlayCanvasRef.current;
                    [baseCanvas, overlayCanvas].forEach((c) => {
                        if (!c) return;
                        c.width = width;
                        c.height = height;
                        const ctx = c.getContext("2d");
                        if (ctx) ctx.imageSmoothingEnabled = true;
                    });
                    const ctx = baseCanvas.getContext("2d");
                    ctx.clearRect(0, 0, width, height);
                    ctx.drawImage(fimg, 0, 0, width, height);
                };
                fimg.src = reader.result;
            };
            reader.readAsDataURL(imageFile);
        };
        img.src = objectUrl;
        return () => {
            try {
                URL.revokeObjectURL(objectUrl);
            } catch {}
        };
    }, [imageFile]);

    const replay = (ctx) => {
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        actions.forEach((a) => drawAction(ctx, a));
        if (activeDraft) {
            drawAction(ctx, activeDraft, true);
        }
    };

    const drawAction = (ctx, a, isDraft = false) => {
        if (!a) return;
        switch (a.type) {
            case "pen": {
                ctx.strokeStyle = a.color;
                ctx.lineWidth = a.width;
                ctx.lineJoin = "round";
                ctx.lineCap = "round";
                ctx.beginPath();
                a.points.forEach((p, i) => {
                    if (i === 0) ctx.moveTo(p.x, p.y);
                    else ctx.lineTo(p.x, p.y);
                });
                ctx.stroke();
                break;
            }
            case "highlight": {
                ctx.save();
                ctx.globalAlpha = 0.35;
                ctx.fillStyle = a.color;
                const x = Math.min(a.start.x, a.end.x);
                const y = Math.min(a.start.y, a.end.y);
                const w = Math.abs(a.start.x - a.end.x);
                const h = Math.abs(a.start.y - a.end.y);
                ctx.fillRect(x, y, w, h);
                ctx.restore();
                break;
            }
            case "arrow": {
                const headlen = Math.max(8, a.width * 3);
                const dx = a.end.x - a.start.x;
                const dy = a.end.y - a.start.y;
                const angle = Math.atan2(dy, dx);
                ctx.strokeStyle = a.color;
                ctx.lineWidth = a.width;
                ctx.lineCap = "round";
                ctx.beginPath();
                ctx.moveTo(a.start.x, a.start.y);
                ctx.lineTo(a.end.x, a.end.y);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(a.end.x, a.end.y);
                ctx.lineTo(
                    a.end.x - headlen * Math.cos(angle - Math.PI / 6),
                    a.end.y - headlen * Math.sin(angle - Math.PI / 6)
                );
                ctx.lineTo(
                    a.end.x - headlen * Math.cos(angle + Math.PI / 6),
                    a.end.y - headlen * Math.sin(angle + Math.PI / 6)
                );
                ctx.lineTo(a.end.x, a.end.y);
                ctx.fillStyle = a.color;
                ctx.fill();
                break;
            }
            case "text": {
                ctx.fillStyle = a.color;
                ctx.font = `bold ${a.size}px Arial`;
                ctx.textBaseline = "top";
                ctx.fillText(a.value, a.x, a.y);
                break;
            }
            default:
                break;
        }
    };

    const onPointerDown = (e) => {
        if (pendingText.visible && tool !== "text") {
            setPendingText({ visible: false, x: 0, y: 0, value: "" });
        }
        const { x, y } = getCanvasPoint(e);
        setRedoStack([]);
        if (tool === "pen") {
            setActiveDraft({
                type: "pen",
                color: strokeColor,
                width: strokeWidth,
                points: [{ x, y }],
            });
        } else if (tool === "highlight") {
            setActiveDraft({
                type: "highlight",
                color: highlightColor,
                start: { x, y },
                end: { x, y },
            });
        } else if (tool === "arrow") {
            setActiveDraft({
                type: "arrow",
                color: strokeColor,
                width: strokeWidth,
                start: { x, y },
                end: { x, y },
            });
        } else if (tool === "text") {
            setPendingText({ visible: true, x, y, value: "" });
        }
        setIsPointerDown(true);
    };

    const onPointerMove = (e) => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        if (!isPointerDown) {
            // Only need to refresh drafts when dragging
            return;
        }
        const { x, y } = getCanvasPoint(e);
        setActiveDraft((draft) => {
            if (!draft) return draft;
            if (draft.type === "pen") {
                const updated = { ...draft, points: [...draft.points, { x, y }] };
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx);
                return updated;
            }
            if (draft.type === "highlight" || draft.type === "arrow") {
                const updated = { ...draft, end: { x, y } };
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx);
                return updated;
            }
            return draft;
        });
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx);
    };

    const onPointerUp = () => {
        if (!isPointerDown) return;
        setIsPointerDown(false);
        if (tool === "text") return;
        if (activeDraft) {
            setActions((prev) => [...prev, activeDraft]);
            setActiveDraft(null);
        }
        const ctx = overlayCanvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx);
    };

    const getCanvasPoint = (e) => {
        const canvas = overlayCanvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * imageSize.width;
        const y = ((e.clientY - rect.top) / rect.height) * imageSize.height;
        return { x, y };
    };

    const commitText = () => {
        if (!pendingText.visible || !pendingText.value.trim()) {
            setPendingText({ visible: false, x: 0, y: 0, value: "" });
            return;
        }
        const action = {
            type: "text",
            color: strokeColor,
            size: Math.max(14, strokeWidth * 5),
            x: pendingText.x,
            y: pendingText.y,
            value: pendingText.value.trim(),
        };
        setActions((prev) => [...prev, action]);
        setPendingText({ visible: false, x: 0, y: 0, value: "" });
        const ctx = overlayCanvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx);
    };

    const handleUndo = () => {
        setActions((prev) => {
            if (prev.length === 0) return prev;
            const next = [...prev];
            const popped = next.pop();
            setRedoStack((r) => [...r, popped]);
            const ctx = overlayCanvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, imageSize.width, imageSize.height);
            replay(ctx);
            return next;
        });
    };

    const handleRedo = () => {
        setRedoStack((prev) => {
            if (prev.length === 0) return prev;
            const next = [...prev];
            const action = next.pop();
            setActions((a) => {
                const combined = [...a, action];
                const ctx = overlayCanvasRef.current.getContext("2d");
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx);
                return combined;
            });
            return next;
        });
    };

    const handleSave = async (mode) => {
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = imageSize.width;
        exportCanvas.height = imageSize.height;
        const ctx = exportCanvas.getContext("2d");
        ctx.drawImage(baseCanvasRef.current, 0, 0);
        // Draw all actions (final render)
        actions.forEach((a) => drawAction(ctx, a));
        if (activeDraft) drawAction(ctx, activeDraft);
        await new Promise((resolve) => {
            exportCanvas.toBlob(
                (blob) => {
                    onSave(blob, { replaceOriginal: mode === "replace" });
                    resolve();
                },
                "image/png",
                0.95
            );
        });
    };

    useEffect(() => {
        if (pendingText.visible && textInputRef.current) {
            textInputRef.current.focus();
        }
    }, [pendingText.visible]);

    // Scale to fit viewport while keeping native resolution on canvas
    const computeStyleSize = () => {
        const maxW = Math.min(window.innerWidth - 48, 1200);
        const maxH = Math.min(window.innerHeight - 200, 800);
        const { width, height } = imageSize;
        if (!width || !height) {
            const fallbackW = Math.min(maxW, 800);
            return { width: fallbackW, height: 320 };
        }
        const ratio = Math.min(maxW / width, maxH / height, 1);
        return { width: width * ratio, height: height * ratio };
    };

    const styleSize = computeStyleSize();

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center"
        >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-[90vw]">
                <div className="flex items-center gap-2 p-3 border-b">
                    <span className="font-medium mr-2 truncate">
                        Annotate: {imageFile.name}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            className={`px-2 py-1 rounded ${tool === "pen" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("pen")}
                            title="Pen"
                        >
                            <Pencil size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "highlight" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("highlight")}
                            title="Highlight"
                        >
                            <Highlighter size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "arrow" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("arrow")}
                            title="Arrow"
                        >
                            <ArrowRight size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "text" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("text")}
                            title="Text"
                        >
                            <Type size={18} />
                        </button>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <label className="text-xs text-gray-500">Color</label>
                        <input
                            type="color"
                            value={strokeColor}
                            onChange={(e) => setStrokeColor(e.target.value)}
                            className="w-6 h-6"
                        />
                        <label className="text-xs text-gray-500 ml-2">Size</label>
                        <input
                            type="range"
                            min="2"
                            max="16"
                            value={strokeWidth}
                            onChange={(e) => setStrokeWidth(parseInt(e.target.value, 10))}
                        />
                        <label className="text-xs text-gray-500 ml-2">Highlight</label>
                        <input
                            type="color"
                            value={highlightColor}
                            onChange={(e) => setHighlightColor(e.target.value)}
                            className="w-6 h-6"
                        />
                    </div>
                    <div className="flex-1" />
                    <div className="flex items-center gap-2">
                        <button
                            className="px-2 py-1 rounded hover:bg-gray-100"
                            onClick={handleUndo}
                            title="Undo"
                        >
                            <RotateCcw size={18} />
                        </button>
                        <button
                            className="px-2 py-1 rounded hover:bg-gray-100"
                            onClick={handleRedo}
                            title="Redo"
                        >
                            <RotateCw size={18} />
                        </button>
                        <button
                            className="px-2 py-1 rounded hover:bg-gray-100"
                            onClick={onClose}
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="p-3 flex items-center justify-center">
                    <div
                        className="relative min-h-[200px]"
                        style={{ width: styleSize.width, height: styleSize.height }}
                    >
                        <canvas
                            ref={baseCanvasRef}
                            className="absolute inset-0 w-full h-full"
                            style={{ touchAction: "none" }}
                        />
                        <canvas
                            ref={overlayCanvasRef}
                            className="absolute inset-0 w-full h-full cursor-crosshair"
                            onMouseDown={onPointerDown}
                            onMouseMove={onPointerMove}
                            onMouseUp={onPointerUp}
                            onMouseLeave={onPointerUp}
                            style={{ touchAction: "none" }}
                        />
                        {pendingText.visible && (
                            <input
                                ref={textInputRef}
                                value={pendingText.value}
                                onChange={(e) =>
                                    setPendingText((p) => ({ ...p, value: e.target.value }))
                                }
                                onBlur={commitText}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitText();
                                    if (e.key === "Escape")
                                        setPendingText({ visible: false, x: 0, y: 0, value: "" });
                                }}
                                className="absolute bg-white border rounded px-2 py-1 text-sm shadow"
                                style={{
                                    left: (pendingText.x / imageSize.width) * 100 + "%",
                                    top: (pendingText.y / imageSize.height) * 100 + "%",
                                    transform: "translate(-0%, -0%)",
                                }}
                                placeholder="Type text…"
                            />
                        )}
                    </div>
                </div>

                <div className="p-3 border-t flex items-center justify-end gap-2">
                    <button
                        onClick={() => handleSave("new")}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                        title="Save as new image"
                    >
                        <FilePlus2 size={18} />
                        Save as New
                    </button>
                    <button
                        onClick={() => handleSave("replace")}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        title="Replace original attachment"
                    >
                        <Save size={18} />
                        Save & Replace
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageAnnotatorModal;


